'use client';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { isUndefined } from 'lodash';
import LogRocket from 'logrocket';
import React, { Fragment } from 'react';

export const initializeBugsnag = ({
  releaseStage,
  appVersion,
  apiKey,
}: {
  apiKey: string;
  appVersion: string;
  releaseStage: string;
}) => {
  /**
   * This if prevents Bugnsnag from possibly running more than once.
   * This usually happens while in the dev environment.
   * @see https://github.com/bugsnag/bugsnag-js/issues/811#issuecomment-703853628
   */
  // @ts-expect-error - Bugsnag._client is not a documented property
  if (!Bugsnag._client) {
    if (apiKey) {
      Bugsnag.start({
        apiKey,
        appVersion,
        logger: null,
        onError: (event) => {
          // events to ignore
          if (event.originalError === 'ResizeObserver loop limit exceeded') {
            return false;
          }

          if (typeof window !== 'undefined') {
            /**
             * Disable Bugsnag in localhost
             */
            if (window.location.hostname === 'localhost') {
              return false;
            }

            if (window.hasLogRocketInitialized) {
              event.addMetadata('LogRocket', {
                sessionURL: LogRocket.sessionURL,
              });
            }

            const isInIframe = window.top !== window.self;

            if (isInIframe) {
              event.addMetadata('isInIframe', {
                isInIframe,
                // document.location.ancestorOrigins is not supported on Firefox, I couldn't find a replacement
                parentLocation: document.location.ancestorOrigins ? document.location.ancestorOrigins[0] : 'Unknown',
              });
            }
          }
        },
        plugins: [new BugsnagPluginReact(React)],
        releaseStage,
      });
    } else {
      console.error('Bugsnag API key not found. Please set NEXT_PUBLIC_BUGSNAG in your .env file.');
    }
  }
};

const BugsnagReactPlugin = Bugsnag.getPlugin('react');

export const ErrorBoundary = isUndefined(BugsnagReactPlugin) ? Fragment : BugsnagReactPlugin.createErrorBoundary(React);

export { Bugsnag };
